import { useState } from "react";
import { Card, CardHeader } from "../../design-system/Card";
import { Badge } from "../../design-system/Badge";
import { Button } from "../../design-system/Button";
import { StatCard } from "../../design-system/StatCard";
import { Icon, type IconName } from "../../design-system/Icon";
import { LoadingState, ErrorState } from "../../components/States";
import { useIpc, useIpcQuery } from "../../ipc/IpcProvider";
import { useConfirm } from "../../components/ConfirmDialog";
import { firstProblem, minLen, isPositiveAmount } from "../../components/validators";
import type { EngineOverviewDto, ShipmentAction, PeriodRange } from "../../ipc/types";
import { useDateRange } from "../../components/DateRangeContext";
import { formatRange } from "../../lib/date-range";
import { CopyDataButton } from "../../components/CopyDataButton";
import "../dashboard/dashboard.css";
import "../product-development/product-development.css";

/** Acciones disponibles según el estado mostrado en la fila. */
function actionsFor(statusLabel: string): ShipmentAction[] {
  if (statusLabel === "Listo") return ["dispatch", "cancel"];
  if (statusLabel === "Despachado" || statusLabel === "En tránsito") return ["deliver", "cancel"];
  return [];
}
const ACTION_LABEL: Record<ShipmentAction, string> = { dispatch: "Despachar", deliver: "Confirmar entrega", cancel: "Cancelar" };

function NewShipmentForm({ onCreate, onCancel, busy }: { onCreate: (v: { destinationCity: string; weightGrams: number }) => void; onCancel: () => void; busy: boolean }): JSX.Element {
  const [city, setCity] = useState("");
  const [kg, setKg] = useState("1");
  const kgNum = Number(kg);
  const hint = firstProblem([
    { ok: minLen(city, 2), msg: "Ingresa la ciudad de destino" },
    { ok: isPositiveAmount(kg), msg: "El peso debe ser un número mayor que 0" },
  ]);
  const valid = hint === null;
  return (
    <Card style={{ marginBottom: "var(--sp-5)" }}>
      <CardHeader title="Nuevo despacho" subtitle="Sale desde Bodega Santiago; se prepara y queda Listo para despachar" />
      <div className="card__body">
        <div className="pd-form">
          <div className="pd-field"><span className="pd-field__label">Ciudad de destino</span>
            <input className="pd-input" value={city} placeholder="Ej: Antofagasta" aria-label="Ciudad de destino" onChange={(e) => setCity(e.target.value)} /></div>
          <div className="pd-field"><span className="pd-field__label">Peso (kg)</span>
            <input className="pd-input" value={kg} type="number" min="0" step="0.1" aria-label="Peso" onChange={(e) => setKg(e.target.value)} /></div>
        </div>
        <div className="pd-actions">
          {hint ? <span className="faint" style={{ fontSize: 12.5, marginRight: "auto" }}>{hint}</span> : null}
          <Button variant="ghost" size="sm" onClick={onCancel}>Cancelar</Button>
          <Button size="sm" icon="check" disabled={!valid || busy} onClick={() => onCreate({ destinationCity: city.trim(), weightGrams: Math.round(kgNum * 1000) })}>
            {busy ? "Creando…" : "Crear despacho"}
          </Button>
        </div>
      </div>
    </Card>
  );
}

export function LogisticsScreen(): JSX.Element {
  const client = useIpc();
  const { range, previous, compare } = useDateRange();
  const periodRange: PeriodRange = {
    fromEpoch: range.fromEpoch, toEpoch: range.toEpoch,
    prevFromEpoch: compare ? previous.fromEpoch : 0,
    prevToEpoch: compare ? previous.toEpoch : 0,
  };
  const { state, reload } = useIpcQuery<EngineOverviewDto>(
    (c) => c.getEngineOverview("logistics", periodRange),
    [range.fromEpoch, range.toEpoch, previous.fromEpoch, previous.toEpoch, compare],
  );
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const { ask, node } = useConfirm();

  if (state.status === "loading") return <LoadingState />;
  if (state.status === "error") return <ErrorState message={state.error.message} onRetry={reload} />;

  const data = state.data;

  const create = async (v: { destinationCity: string; weightGrams: number }): Promise<void> => {
    setBusy(true); setMsg(null);
    const res = await client.createShipment(v);
    setBusy(false);
    if (res.ok && res.value.ok) { setShowForm(false); setMsg(`Despacho a ${v.destinationCity} creado.`); reload(); }
    else setMsg(res.ok ? res.value.message ?? "No se pudo crear" : res.error.message);
  };
  const act = async (id: string, action: ShipmentAction): Promise<void> => {
    if (action === "cancel") {
      const ok = await ask({ title: "Cancelar despacho", message: "El despacho quedará cancelado. Esta acción no se puede deshacer. ¿Continuar?", confirmLabel: "Sí, cancelar", danger: true });
      if (!ok) return;
    }
    setBusy(true); setMsg(null);
    const res = await client.setShipmentStatus(id, action);
    setBusy(false);
    if (res.ok && res.value.ok) reload();
    else setMsg(res.ok ? res.value.message ?? "Acción no permitida" : res.error.message);
  };

  return (
    <div>
      {node}
      <div className="pagehead">
        <div className="pagehead__row">
          <div className="row" style={{ gap: "var(--sp-4)" }}>
            <div className="sidebar__logo" style={{ width: 44, height: 44 }}><Icon name="logistics" size={22} /></div>
            <div>
              <h1 className="pagehead__title">Logística</h1>
              <p className="pagehead__sub">Despachos a tus clientes. Crea, despacha y confirma entregas; se guardan en tu equipo.</p>
              {data.period ? (
                <div className="row" style={{ gap: 8, marginTop: 4 }}>
                  <Badge tone="brand" dot>{formatRange({ fromEpoch: data.period.fromEpoch, toEpoch: data.period.toEpoch })}</Badge>
                  {compare ? <span className="faint" style={{ fontSize: 12 }}>vs período anterior</span> : null}
                </div>
              ) : null}
            </div>
          </div>
          <div className="row">
            <CopyDataButton data={{ title: data.title, columns: data.columns, rows: data.rows, metrics: data.metrics, ...(data.period ? { periodLabel: formatRange(data.period) } : {}) }} />
            <Button size="sm" icon="logistics" onClick={() => setShowForm((s) => !s)}>Nuevo despacho</Button>
          </div>
        </div>
      </div>

      <div className="kpis" style={{ marginTop: 0 }}>
        {data.metrics.map((m) => (
          <StatCard key={m.label} label={m.label} value={m.value} icon={m.icon as IconName} accent={m.accent}
            {...(typeof m.deltaPct === "number" ? { deltaPct: m.deltaPct } : {})} />
        ))}
      </div>

      {showForm ? <div style={{ marginTop: "var(--sp-5)" }}><NewShipmentForm onCreate={create} onCancel={() => setShowForm(false)} busy={busy} /></div> : null}

      <Card style={{ marginTop: "var(--sp-5)" }}>
        <CardHeader title="Despachos" subtitle={`${data.rows.length} despachos`} action={msg ? <span className="muted" style={{ fontSize: 12.5 }}>{msg}</span> : null} />
        <div className="card__body" style={{ padding: 0 }}>
          {data.rows.length === 0 ? (
            <div className="statecard"><Icon name="logistics" size={26} style={{ color: "var(--text-faint)" }} /><div className="strong">Aún no tienes despachos</div><div className="faint" style={{ fontSize: 13 }}>Crea el primero con “Nuevo despacho”.</div></div>
          ) : (
            <table className="table">
              <thead><tr><th>Despacho</th><th>Destino</th><th>Estado</th><th>Peso</th><th>Acciones</th></tr></thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.id}>
                    <td><div className="strong">{r.primary}</div></td>
                    <td className="muted">{r.secondary}</td>
                    <td><Badge tone={r.status.tone} dot>{r.status.label}</Badge></td>
                    <td className="strong">{r.amount}</td>
                    <td>
                      <div className="row" style={{ gap: 6 }}>
                        {actionsFor(r.status.label).map((a) => (
                          <Button key={a} variant={a === "cancel" ? "ghost" : "secondary"} size="sm" disabled={busy} onClick={() => act(r.id, a)}>{ACTION_LABEL[a]}</Button>
                        ))}
                        {actionsFor(r.status.label).length === 0 ? <span className="faint" style={{ fontSize: 12 }}>—</span> : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Card>
    </div>
  );
}
